import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Lottie from 'lottie-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent, trackEcommerce } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowRight, Crown, GraduationCap, Check } from 'lucide-react';
import { PublicHeader } from '@/components/layout/PublicHeader';
import { PublicFooter } from '@/components/layout/PublicFooter';
import signupHeroAnimation from '@/assets/signup-hero.json';
import signupIcon from '@/assets/signup-icon.svg';
import { z } from 'zod';
const authSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});
type PlanType = 'student' | 'mastery';
type BillingCycle = 'annual' | 'monthly';
interface PlanOption {
  id: PlanType;
  name: string;
  icon: typeof GraduationCap;
  monthlyPrice: string;
  annualPrice: string;
  annualMonthly: string;
  features: string[];
  color: string;
  hasMonthly: boolean;
}
const planOptions: PlanOption[] = [{
  id: 'student',
  name: 'Student',
  icon: GraduationCap,
  monthlyPrice: '$19.99',
  annualPrice: '$239.88',
  annualMonthly: '$19.99',
  features: ['100+ lessons', 'Progress tracking', 'Bookmarks & playlists'],
  color: 'blue',
  hasMonthly: false // Student is annual only
}, {
  id: 'mastery',
  name: 'Mastery',
  icon: Crown,
  monthlyPrice: '$29.99',
  annualPrice: '$299.88',
  annualMonthly: '$24.99',
  features: ['All 400+ lessons', 'A/B loop tools', 'Priority support'],
  color: 'gold',
  hasMonthly: true
}];
export default function Signup() {
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan') as PlanType | null;
  const billingFromUrl = searchParams.get('billing') as BillingCycle | null;
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(planFromUrl || 'student');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(billingFromUrl || 'annual');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const {
    user,
    profile,
    isAdmin
  } = useAuth();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  // Only redirect logged-in users with an active subscription to dashboard
  // Lapsed/canceled users should NOT be redirected — they need to resubscribe
  useEffect(() => {
    if (user && profile) {
      const paidPlans = ['admin', 'mastery', 'student'];
      const hasActiveSubscription =
        isAdmin ||
        profile.membership_plan === 'admin' ||
        (profile.membership_status === 'active' && paidPlans.includes(profile.membership_plan));
      if (hasActiveSubscription) {
        navigate('/dashboard');
      }
    }
  }, [user, profile, isAdmin, navigate]);

  // Check for canceled checkout
  useEffect(() => {
    const canceled = searchParams.get('canceled');
    if (canceled === 'true') {
      toast({
        variant: 'destructive',
        title: 'Checkout canceled',
        description: 'Your signup was not completed. Please try again.'
      });
    }
  }, [searchParams, toast]);
  const validateForm = () => {
    try {
      authSchema.parse({
        email,
        password
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: {
          email?: string;
          password?: string;
        } = {};
        err.errors.forEach(error => {
          if (error.path[0] === 'email') fieldErrors.email = error.message;
          if (error.path[0] === 'password') fieldErrors.password = error.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    trackEvent('sign_up_started', { plan: selectedPlan, billing_cycle: billingCycle });

    // Fire standard GA4 begin_checkout event
    const price = billingCycle === 'monthly' && currentPlanOption.hasMonthly
      ? parseFloat(currentPlanOption.monthlyPrice.replace('$', ''))
      : parseFloat(currentPlanOption.annualPrice.replace('$', ''));
    trackEcommerce('begin_checkout', {
      currency: 'USD',
      value: price,
      items: [{
        item_id: selectedPlan,
        item_name: `${currentPlanOption.name} Plan`,
        price,
        quantity: 1,
        item_category: billingCycle,
      }],
    });
    try {
      // Call pending-checkout to store data and create Stripe session
      const {
        data,
        error
      } = await supabase.functions.invoke('pending-checkout', {
        body: {
          email,
          password,
          displayName: displayName || null,
          plan: selectedPlan,
          billingCycle
        }
      });
      if (error) throw error;
      if (data?.url) {
        trackEvent('sign_up_checkout_redirect', { plan: selectedPlan, billing_cycle: billingCycle });
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: unknown) {
      console.error('Signup failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      if (errorMessage.includes('already exists')) {
        toast({
          variant: 'destructive',
          title: 'Account exists',
          description: 'An account with this email already exists. Please sign in instead.'
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Signup failed',
          description: errorMessage
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const currentPlanOption = planOptions.find(p => p.id === selectedPlan) || planOptions[0];
  const displayPrice = billingCycle === 'monthly' && currentPlanOption.hasMonthly ? currentPlanOption.monthlyPrice : currentPlanOption.annualMonthly;
  return <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Sign Up - Tenacity Music</title>
        <meta name="description" content="Create your Tenacity Music account and start learning jazz improvisation from world-class artists." />
      </Helmet>
      <PublicHeader />
      
      <main className="flex-1 pt-20">
        {/* Hero Section with Form */}
        <section className="container mx-auto px-4 pt-2 md:pt-10 pb-0">
          {/* Desktop/Tablet Layout with Animation */}
          <div className="hidden md:flex flex-col lg:flex-row gap-0 items-stretch rounded-t-[140px] overflow-hidden animate-hero-card" style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 38.93%, rgba(0, 0, 0, 0.1) 161.95%)'
        }}>
            {/* Left - Hero Animation */}
            <div className="w-full lg:w-2/5 min-h-[300px] lg:min-h-0 lg:self-stretch relative overflow-hidden">
              <Lottie animationData={signupHeroAnimation} loop={false} autoplay={true} className="absolute inset-0 w-full h-full" rendererSettings={{
              preserveAspectRatio: 'xMidYMid slice'
            }} />
            </div>

            {/* Right - Form */}
            <div className="w-full lg:w-3/5 p-8 md:p-12 lg:p-16 flex flex-col justify-center animate-hero-content">
              <h1 className="page-hero-title mb-2">
                <span className="text-[#FFFF00]">Your Learning</span>
                <br />
                <span className="text-white">Starts Here</span>
              </h1>
              <p className="text-white mb-6">Create an account to start your jazz journey.</p>

              {/* Plan Selection */}
              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-3">Select your plan</p>
                <div className="grid grid-cols-2 gap-3">
                  {planOptions.map(plan => <button key={plan.id} type="button" onClick={() => {
                  setSelectedPlan(plan.id);
                  if (!plan.hasMonthly) setBillingCycle('annual');
                }} className={`relative p-4 rounded-xl border-2 text-left transition-all ${selectedPlan === plan.id ? plan.id === 'mastery' ? 'border-tenacity-gold bg-tenacity-gold/10' : 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-muted-foreground'}`}>
                      {selectedPlan === plan.id && <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${plan.id === 'mastery' ? 'bg-tenacity-gold' : 'bg-blue-500'}`}>
                          <Check className="w-3 h-3 text-black" />
                        </div>}
                      <plan.icon className={`w-5 h-5 mb-2 ${plan.id === 'mastery' ? 'text-tenacity-gold' : 'text-blue-400'}`} />
                      <p className="font-semibold text-white">{plan.name}</p>
                      <p className={`text-sm ${plan.id === 'mastery' ? 'text-tenacity-gold' : 'text-blue-400'}`}>
                        {plan.hasMonthly && billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualMonthly}/mo
                      </p>
                    </button>)}
                </div>
              </div>

              {/* Billing Toggle - Only show for Mastery */}
              {currentPlanOption.hasMonthly && <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-3">Billing cycle</p>
                  <div className="inline-flex rounded-full p-1 bg-white/10">
                    <button type="button" onClick={() => setBillingCycle('annual')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'annual' ? 'bg-tenacity-gold text-black' : 'text-white/70 hover:text-white'}`}>
                      Annual (Save $60)
                    </button>
                    <button type="button" onClick={() => setBillingCycle('monthly')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-tenacity-gold text-black' : 'text-white/70 hover:text-white'}`}>
                      Monthly
                    </button>
                  </div>
                </div>}

              {/* Selected plan summary */}
              <Badge className={`mb-6 inline-flex items-center gap-1.5 w-fit cursor-default hover:bg-opacity-100 ${selectedPlan === 'mastery' ? 'bg-tenacity-gold/20 text-tenacity-gold border-tenacity-gold/30 hover:bg-tenacity-gold/20' : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/20'}`}>
                <currentPlanOption.icon className="w-3.5 h-3.5" />
                {currentPlanOption.name} - {displayPrice}/mo {billingCycle === 'annual' ? '(billed annually)' : ''}
              </Badge>

              <form onSubmit={handleSignUp} className="space-y-6">
                <div className="space-y-2">
                  <Input type="text" placeholder="Full Name" value={displayName} onChange={e => setDisplayName(e.target.value)} className={`bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#FFFF00] transition-colors ${displayName ? 'border-[#FFFF00]' : 'border-border'}`} />
                </div>

                <div className="space-y-2">
                  <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className={`bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#FFFF00] transition-colors ${email ? 'border-[#FFFF00]' : 'border-border'} ${errors.email ? 'border-destructive' : ''}`} />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
                
                <div className="space-y-2">
                  <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className={`bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#FFFF00] transition-colors ${password ? 'border-[#FFFF00]' : 'border-border'} ${errors.password ? 'border-destructive' : ''}`} />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>

                <Button type="submit" className="w-full h-12 rounded-lg text-button uppercase bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F59E0B] hover:opacity-90 transition-opacity" disabled={isLoading}>
                  {isLoading ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </> : <>
                      Create Account & Subscribe
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  You'll be redirected to complete payment after creating your account
                </p>
              </form>

              <div className="mt-4 text-center">
                <Link to="/plans" className="text-sm text-muted-foreground hover:text-foreground">
                  ← View full plan comparison
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile Layout - No Animation, Centered */}
          <div className="md:hidden flex flex-col items-center px-4 py-4">
            <h1 className="text-hero text-center mb-4">
              <span className="text-[#FFFF00]">YOUR LEARNING</span>{' '}
              <span className="text-white">STARTS HERE</span>
            </h1>
            <p className="text-white mb-6 text-center">Create an account to start your jazz journey.</p>

            {/* Plan Selection */}
            <div className="mb-6 w-full max-w-sm">
              <p className="text-sm text-muted-foreground mb-3 text-center">Select your plan</p>
              <div className="grid grid-cols-2 gap-3">
                {planOptions.map(plan => <button key={plan.id} type="button" onClick={() => {
                setSelectedPlan(plan.id);
                if (!plan.hasMonthly) setBillingCycle('annual');
              }} className={`relative p-4 rounded-xl border-2 text-left transition-all ${selectedPlan === plan.id ? plan.id === 'mastery' ? 'border-tenacity-gold bg-tenacity-gold/10' : 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-muted-foreground'}`}>
                    {selectedPlan === plan.id && <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${plan.id === 'mastery' ? 'bg-tenacity-gold' : 'bg-blue-500'}`}>
                        <Check className="w-3 h-3 text-black" />
                      </div>}
                    <plan.icon className={`w-5 h-5 mb-2 ${plan.id === 'mastery' ? 'text-tenacity-gold' : 'text-blue-400'}`} />
                    <p className="font-semibold text-white">{plan.name}</p>
                    <p className={`text-sm ${plan.id === 'mastery' ? 'text-tenacity-gold' : 'text-blue-400'}`}>
                      {plan.hasMonthly && billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualMonthly}/mo
                    </p>
                  </button>)}
              </div>
            </div>

            {/* Billing Toggle - Only show for Mastery */}
            {currentPlanOption.hasMonthly && <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-3 text-center">Billing cycle</p>
                <div className="inline-flex rounded-full p-1 bg-white/10">
                  <button type="button" onClick={() => setBillingCycle('annual')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'annual' ? 'bg-tenacity-gold text-black' : 'text-white/70 hover:text-white'}`}>
                    Annual (Save $60)
                  </button>
                  <button type="button" onClick={() => setBillingCycle('monthly')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-tenacity-gold text-black' : 'text-white/70 hover:text-white'}`}>
                    Monthly
                  </button>
                </div>
              </div>}

            {/* Selected plan summary */}
            <Badge className={`mb-6 inline-flex items-center gap-1.5 w-fit cursor-default hover:bg-opacity-100 ${selectedPlan === 'mastery' ? 'bg-tenacity-gold/20 text-tenacity-gold border-tenacity-gold/30 hover:bg-tenacity-gold/20' : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/20'}`}>
              <currentPlanOption.icon className="w-3.5 h-3.5" />
              {currentPlanOption.name} - {displayPrice}/mo {billingCycle === 'annual' ? '(billed annually)' : ''}
            </Badge>

            <form onSubmit={handleSignUp} className="space-y-6 w-full max-w-sm">
              <div className="space-y-2">
                <Input type="text" placeholder="Full Name" value={displayName} onChange={e => setDisplayName(e.target.value)} className={`bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#FFFF00] transition-colors ${displayName ? 'border-[#FFFF00]' : 'border-border'}`} />
              </div>

              <div className="space-y-2">
                <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className={`bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#FFFF00] transition-colors ${email ? 'border-[#FFFF00]' : 'border-border'} ${errors.email ? 'border-destructive' : ''}`} />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>
              
              <div className="space-y-2">
                <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className={`bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#FFFF00] transition-colors ${password ? 'border-[#FFFF00]' : 'border-border'} ${errors.password ? 'border-destructive' : ''}`} />
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>

              <Button type="submit" className="w-full h-12 rounded-lg text-button uppercase bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F59E0B] hover:opacity-90 transition-opacity" disabled={isLoading}>
                {isLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </> : <>
                    Create Account & Subscribe
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                You'll be redirected to complete payment after creating your account
              </p>
            </form>

            <div className="mt-4 text-center">
              <Link to="/plans" className="text-sm text-muted-foreground hover:text-foreground">
                ← View full plan comparison
              </Link>
            </div>
          </div>
        </section>

        {/* Already a member? Login */}
        <ScrollReveal>
          <section className="py-6 md:py-[60px] text-center">
            <div className="container max-w-5xl mx-auto px-6 md:px-8">
              <h3 className="text-headline-3 font-display">
                Already a member?{' '}
                <Link to="/login" className="text-[#CB5EFF] inline-flex items-center gap-2 group">
                  Login now
                  <img src={signupIcon} alt="" className="w-10 h-10 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </h3>
            </div>
          </section>
        </ScrollReveal>


        {/* Quote Section */}
        <ScrollReveal delay={150}>
          <section className="py-4 md:py-6 pb-16 md:pb-24 text-center">
            <div className="container max-w-5xl mx-auto px-6 md:px-8">
              <blockquote className="mb-6">
              <p className="quote leading-tight">
                  "The spirit of jazz is the spirit of <span className="text-[#1360DA]">openness</span>."
                </p>
              </blockquote>
              <p className="quote-attribution">
                
                – Herbie Hancock 
               
              </p>
            </div>
          </section>
        </ScrollReveal>
      </main>
      
      <PublicFooter />
    </div>;
}